import React, { useEffect, useState, useCallback } from "react";
import Header from "../../components/Header";
import AddButton from "../../components/AddButton";
import DeleteModal from "../../components/DeleteModal";
import Pagination from "../../components/Pagination";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router";

import Loading from "../../components/Loading";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";

import {
  getWeekWorkoutPrograms,
  deleteWeekWorkoutProgram,
  WeekWorkoutProgram,
  setWeekProgramStatus,
  getWeekProgramCompletionsCount,
} from "../../services/weekWorkoutService";

import { getTrainers, Trainer } from "../../services/trainerService";

const WeekWorkoutsPage: React.FC = () => {
  const navigate = useNavigate();

  const [data, setData] = useState<WeekWorkoutProgram[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<WeekWorkoutProgram | null>(
    null
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [trainerFilter, setTrainerFilter] = useState<number | "">("");

  const [completionCounts, setCompletionCounts] = useState<
    Record<number, number>
  >({});

  // Подгружаем список тренеров
  useEffect(() => {
    const fetchTrainers = async () => {
      try {
        const trainersList = await getTrainers();
        setTrainers(trainersList);
      } catch (err) {
        console.error("Failed to fetch trainers", err);
      }
    };
    fetchTrainers();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getWeekWorkoutPrograms(
        currentPage,
        10,
        trainerFilter
      );
      setData(response.programs);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch Trainer programs");
    } finally {
      setLoading(false);
    }
  }, [currentPage, trainerFilter]);

  console.log(data);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchCounts = async () => {
      const map: Record<number, number> = {};
      for (const program of data) {
        try {
          const count = await getWeekProgramCompletionsCount(program.id);
          map[program.id] = count;
        } catch {
          map[program.id] = 0;
        }
      }
      setCompletionCounts(map);
    };

    if (data.length > 0) fetchCounts();
  }, [data]);

  console.log(completionCounts);

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await deleteWeekWorkoutProgram(itemToDelete.id);
      setSuccessMessage("Program deleted successfully");
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      console.error(err);
      setError("Failed to delete program");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Trainer Programs" />

      <div className="my-4 flex justify-between items-center">
        <select
          className="border p-2 rounded"
          value={trainerFilter}
          onChange={(e) =>
            setTrainerFilter(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">All Trainers</option>
          {trainers.map((trainer) => (
            <option key={trainer.id} value={trainer.id}>
              {trainer.name}
            </option>
          ))}
        </select>

        <AddButton
          text="Add Trainer Program"
          onClick={() => navigate("/add-week-program")}
        />
      </div>

      {loading ? (
        <Loading message="Loading Trainer Programs..." />
      ) : (
        <>
          {successMessage && (
            <SuccessMessage
              message={successMessage}
              onClose={() => setSuccessMessage(null)}
            />
          )}
          {error && (
            <ErrorMessage message={error} onClose={() => setError(null)} />
          )}

          <div className="rounded-lg overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 px-4 text-left">ID</th>
                  <th className="py-2 px-4 text-left">Title</th>
                  <th className="py-2 px-4 text-left">Description</th>
                  <th className="py-2 px-4 text-left">Status</th>
                  <th className="py-2 px-4 text-left">Completed Days</th>
                  <th className="py-2 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">{item.id}</td>
                    <td className="py-3 px-4">{item.title}</td>
                    <td
                      className="py-3 px-4 max-w-xs overflow-hidden text-ellipsis"
                      dangerouslySetInnerHTML={{
                        __html: item.description ?? "",
                      }}
                    />
                    <td className="py-3 px-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={item.isActive}
                          onChange={async (e) => {
                            try {
                              await setWeekProgramStatus(
                                item.id,
                                e.target.checked
                              );
                              setSuccessMessage("Program status updated");
                              fetchData(); // обновим таблицу
                            } catch (err) {
                              console.error(err);
                              setError("Failed to update program status");
                            }
                          }}
                          className="mr-2"
                        />
                        {item.isActive ? "Active" : "Inactive"}
                      </label>
                    </td>
                    <td className="py-3 px-4">
                      {completionCounts[item.id] ?? "..."}
                    </td>
                    <td className="py-3 px-4 flex gap-2">
                      <button
                        onClick={() =>
                          navigate("/edit-week-program", { state: item })
                        }
                        className="px-2 py-1 text-yellow-600 border border-yellow-600 rounded hover:bg-gray-100"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setItemToDelete(item);
                          setIsDeleteModalOpen(true);
                        }}
                        className="px-2 py-1 text-red-600 border border-red-600 rounded hover:bg-gray-100"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName={itemToDelete?.title || ""}
        itemType="program"
      />
    </div>
  );
};

export default WeekWorkoutsPage;
